import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { getMonthlyOverviewDetails } from "../../api/monthlyOverviewApi";
import {
  MonthlyOverviewDetailRow,
  MonthlyOverviewDetailsResponse,
  MonthlyOverviewMetricKey,
} from "../../types/monthlyOverview.type";
import { METRIC_COLUMNS, METRIC_VALUE_FIELD } from "./monthlyOverview.columns";
import { exportMetricDetails } from "./monthlyOverviewExport";

const { Text } = Typography;

// Формат суммы с разделителями тысяч (неразрывный пробел, как в макете).
const fmt = (value: number | undefined) =>
  new Intl.NumberFormat("ru-RU").format(Math.round(value ?? 0));

const fmtQty = (value: number | undefined) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 3 }).format(value ?? 0);

// Строит колонки antd-таблицы из общего конфига METRIC_COLUMNS.
const buildColumns = (
  metric: MonthlyOverviewMetricKey
): ColumnsType<MonthlyOverviewDetailRow> =>
  METRIC_COLUMNS[metric].map((col) => {
    const base: ColumnsType<MonthlyOverviewDetailRow>[number] = {
      title: col.title,
      dataIndex: col.key,
      key: col.key,
      width: col.width,
      ellipsis: col.ellipsis,
    };
    if (col.type === "tag") {
      return { ...base, render: (v: string) => <Tag>{v}</Tag> };
    }
    if (col.type === "qty") {
      return { ...base, align: "right", render: (v: number) => fmtQty(v) };
    }
    if (col.type === "currency") {
      const isValueField = col.key === METRIC_VALUE_FIELD[metric];
      return {
        ...base,
        align: "right",
        render: (v: number) => (isValueField ? <b>{fmt(v)}</b> : fmt(v)),
      };
    }
    return base;
  });

interface Props {
  open: boolean;
  metric: MonthlyOverviewMetricKey | null;
  title: string;
  month: number;
  year: number;
  onClose: () => void;
}

export default function MonthlyOverviewDetailsModal({
  open,
  metric,
  title,
  month,
  year,
  onClose,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MonthlyOverviewDetailsResponse | null>(null);

  useEffect(() => {
    if (!open || !metric) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setData(null);
      try {
        const result = await getMonthlyOverviewDetails(metric, month, year);
        if (!cancelled) setData(result);
      } catch (error) {
        console.error("Ошибка при загрузке расшифровки:", error);
        if (!cancelled) message.error("Не удалось загрузить расшифровку");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, metric, month, year]);

  const columns = useMemo(
    () => (metric ? buildColumns(metric) : []),
    [metric]
  );

  const handleExport = () => {
    if (!data || data.rows.length === 0) {
      message.info("Нет данных для экспорта");
      return;
    }
    exportMetricDetails(data);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ top: 24, maxWidth: 1280 }}
      styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
      title={`${title} — откуда сумма`}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: 32,
              marginBottom: 16,
              alignItems: "center",
            }}
          >
            <Statistic
              title="Итого, UZS"
              value={data?.total ?? 0}
              precision={0}
              groupSeparator=" "
            />
            <Statistic title="Записей" value={data?.count ?? 0} />
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              disabled={!data || data.rows.length === 0}
              style={{ marginLeft: "auto" }}
            >
              Экспорт в Excel
            </Button>
          </div>

          {data?.truncated && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Показаны первые ${data.rows.length} записей. Итог рассчитан по всем (${data.count}).`}
            />
          )}

          <Table<MonthlyOverviewDetailRow>
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={data?.rows ?? []}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            scroll={{ x: true, y: 440 }}
            locale={{ emptyText: "Нет данных за выбранный месяц" }}
            summary={(pageData) => {
              if (!metric || pageData.length === 0) return null;
              const valueField = METRIC_VALUE_FIELD[metric];
              const pageSum = pageData.reduce(
                (sum, row) => sum + ((row as any)[valueField] ?? 0),
                0
              );
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={columns.length - 1}>
                      <Text strong>Итого на странице</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{fmt(pageSum)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </>
      )}
    </Modal>
  );
}
